import React, { useEffect, useMemo, useState, useRef, forwardRef, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { Database, Info, MoreVertical, ChevronDown, Table2, Cpu, Lock } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { RegionSelector } from './RegionSelector';
import { TableActionsMenu } from './TableActionsMenu';
import { TableModelDropdown } from './TableModelDropdown';
import type { ModelOverride } from './ModelRunSelector';
import type { LocationOverride } from './LocationSelector';
import { useWeatherForecast, getVariableValue, getVariableUnit } from '../../hooks/useWeatherData';
import { useENTSOEData, isENTSOEVariable } from '../../hooks/useENTSOE';
const VARIABLES = [
// Weather: Temperature
'Temp 2m', 'Dew Point 2m',
// Weather: Wind
'Wind Speed 10m', 'Wind Dir 10m', 'Wind Speed 100m', 'Wind Dir 100m',
// Weather: Precipitation
'Precipitation 1h',
// Weather: Radiation
'SSRD 1h',
// Weather: Atmosphere
'MSL Pressure',
// Weather: Cloud Cover
'Cloud Cover High', 'Cloud Cover Medium', 'Cloud Cover Low',
// ENTSOE: Prices
'Day-ahead Prices', 'Imbalance Prices',
// ENTSOE: Generation
'Generation (Actual)', 'Generation Forecast (Day-ahead)', 'Wind & Solar Forecast (Day-ahead)', 'Wind & Solar Forecast (Intraday)',
// ENTSOE: Load
'Load (Actual)', 'Load Forecast (Day-ahead)',
// ENTSOE: Capacity & Flows
'Available Hydro Capacity', 'Cross-border Flows', 'NTC (Day-ahead)', 'NTC Weekahead', 'NTC Monthahead', 'NTC Yearahead',
// ENTSOE: Balancing
'Imbalance Volumes', 'Net Position (Day-ahead)', 'Net Position (Total)', 'Scheduled Exchanges (Day-ahead)', 'Scheduled Exchanges (Total)'];
const TIME_GROUPINGS = ['Hourly', 'Daily'];
/** Score a variable name against a search query with numeric proximity */
function scoreVariable(varName: string, query: string): number {
  if (!query.trim()) return 1;
  const q = query.toLowerCase().trim();
  const v = varName.toLowerCase();
  // Exact match
  if (v === q) return 100;
  // Starts with query
  if (v.startsWith(q)) return 80;
  // Contains full query
  if (v.includes(q)) return 70;
  // Token-based scoring
  const qTokens = q.split(/\s+/);
  let score = 0;
  for (const token of qTokens) {
    if (v.includes(token)) score += 10;
  }
  // Numeric proximity bonus: extract numbers from query and variable
  const qNums = q.match(/\d+/g)?.map(Number) || [];
  const vNums = varName.match(/\d+/g)?.map(Number) || [];
  if (qNums.length > 0 && vNums.length > 0) {
    const qNum = qNums[0];
    const vNum = vNums[0];
    // Closer numbers get higher bonus (max 20 for exact, decreasing)
    const diff = Math.abs(qNum - vNum);
    const proximityBonus = diff === 0 ? 20 : Math.max(0, 15 - diff);
    score += proximityBonus;
  }
  return score;
}
function filterAndSortVariables(vars: string[], query: string): string[] {
  if (!query.trim()) return vars;
  return vars.map(v => ({
    v,
    score: scoreVariable(v, query)
  })).filter(({
    score
  }) => score > 0).sort((a, b) => b.score - a.score).map(({
    v
  }) => v);
}
interface ModelOption {
  name: string;
  category: 'ai' | 'nwp' | 'live';
  liveOnly?: boolean;
  hourlyRuns?: boolean;
}
const ALL_MODELS: ModelOption[] = [{
  name: 'EPT-2',
  category: 'ai'
}, {
  name: 'EPT-2 Early',
  category: 'ai'
}, {
  name: 'EPT-2 HRRR',
  category: 'ai',
  hourlyRuns: true
}, {
  name: 'EPT-2e',
  category: 'ai'
}, {
  name: 'EC AIFS',
  category: 'ai'
}, {
  name: 'EC IFS',
  category: 'nwp'
}, {
  name: 'GFS',
  category: 'nwp'
}, {
  name: 'ICON Global',
  category: 'nwp'
}, {
  name: 'ICON-EU',
  category: 'nwp'
}, {
  name: 'EC Ensemble Mean',
  category: 'live',
  liveOnly: true
}, {
  name: 'GFS Ensemble Mean',
  category: 'live',
  liveOnly: true
}];
const MODEL_COLORS: Record<string, string> = {
  'EPT-2': 'bg-[#EC4899]',
  'EPT-1.5': 'bg-blue-500',
  'EC IFS': 'bg-blue-400',
  'EC AIFS': 'bg-emerald-500',
  GFS: 'bg-amber-500',
  'ICON Global': 'bg-violet-500',
  'ICON-EU': 'bg-violet-400'
};
function getModelColor(name: string, index: number): string {
  if (MODEL_COLORS[name]) return MODEL_COLORS[name];
  const fallback = ['bg-cyan-500', 'bg-orange-500', 'bg-rose-500', 'bg-teal-500', 'bg-indigo-500', 'bg-lime-500'];
  return fallback[index % fallback.length];
}
/** A single model+run entry in the widget */
export interface ModelRunEntry {
  id: string; // unique key: "EPT-2" or "EPT-2__<isoString>"
  modelName: string;
  runIso: string; // '' means latest/default
}
function makeEntryId(modelName: string, runIso: string): string {
  return runIso ? `${modelName}__${runIso}` : modelName;
}
function formatRunLabel(runIso: string): string {
  if (!runIso) return '';
  const d = new Date(runIso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  }) + ' UTC';
}
function formatRunShort(runIso: string): string {
  if (!runIso) return '';
  const d = new Date(runIso);
  return d.getUTCHours().toString().padStart(2, '0') + 'z';
}
/** Get the latest available run ISO for a model */
function getLatestRunIso(hourly?: boolean): string {
  const now = new Date();
  if (hourly) {
    // Round down to current hour
    now.setUTCMinutes(0, 0, 0);
  } else {
    // Round down to nearest 6h block
    const hour = Math.floor(now.getUTCHours() / 6) * 6;
    now.setUTCHours(hour, 0, 0, 0);
  }
  return now.toISOString();
}
/** Check if a model has hourly runs */
function isHourlyModel(name: string): boolean {
  return name.includes('HRRR');
}
/** Convert entries array to the map format the dropdown expects */
function entriesToModelRunsMap(entries: ModelRunEntry[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const e of entries) {
    if (!map[e.modelName]) map[e.modelName] = [];
    map[e.modelName].push(e.runIso);
  }
  return map;
}
interface TableWidgetProps {
  id: string;
  onRemove?: () => void;
  onDuplicate?: () => void;
  onParameterChange?: () => void;
  onDoubleClickHandle?: () => void;
  modelOverride?: ModelOverride | null;
  locationOverride?: LocationOverride | null;
  readOnly?: boolean;
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchEnd?: React.TouchEventHandler;
}
export const TableWidget = forwardRef<HTMLDivElement, TableWidgetProps>(({
  id,
  onRemove,
  onDuplicate,
  onParameterChange,
  onDoubleClickHandle,
  modelOverride,
  locationOverride,
  readOnly,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  ...props
}, ref) => {
  const [region, setRegion] = useState('Germany');
  const [variable, setVariable] = useState('Wind Speed 100m');
  const [timeGrouping, setTimeGrouping] = useState('Hourly');
  // New multi-run data model
  const [entries, setEntries] = useState<ModelRunEntry[]>(() => {
    const defaultModels = [{
      name: 'EPT-2',
      hourly: false
    }, {
      name: 'EPT-2 HRRR',
      hourly: true
    }, {
      name: 'EC AIFS',
      hourly: false
    }, {
      name: 'EC IFS',
      hourly: false
    }];
    return defaultModels.map(m => {
      const runIso = getLatestRunIso(m.hourly);
      return {
        id: makeEntryId(m.name, runIso),
        modelName: m.name,
        runIso
      };
    });
  });
  const preOverrideEntriesRef = useRef<ModelRunEntry[] | null>(null);
  // Handle override changes
  useEffect(() => {
    if (modelOverride) {
      if (!preOverrideEntriesRef.current) {
        preOverrideEntriesRef.current = [...entries];
      }
      const overrideEntries: ModelRunEntry[] = modelOverride.runs.map(run => ({
        id: makeEntryId(run.modelName, new Date(run.timestamp).toISOString()),
        modelName: run.modelName,
        runIso: new Date(run.timestamp).toISOString()
      }));
      setEntries(overrideEntries);
    } else if (preOverrideEntriesRef.current) {
      setEntries(preOverrideEntriesRef.current);
      preOverrideEntriesRef.current = null;
    }
  }, [modelOverride]);
  // Handle location override
  const preOverrideRegionRef = useRef<string | null>(null);
  useEffect(() => {
    if (locationOverride) {
      if (!preOverrideRegionRef.current) {
        preOverrideRegionRef.current = region;
      }
      setRegion(locationOverride.regionName);
    } else if (preOverrideRegionRef.current) {
      setRegion(preOverrideRegionRef.current);
      preOverrideRegionRef.current = null;
    }
  }, [locationOverride]);
  const availableRuns = useMemo(() => {
    const runs: {
      label: string;
      value: string;
      date: Date;
    }[] = [];
    const now = new Date();
    for (let dayOffset = 0; dayOffset >= -1; dayOffset--) {
      const day = new Date(now);
      day.setUTCDate(day.getUTCDate() + dayOffset);
      for (const hour of [18, 12, 6, 0]) {
        const runDate = new Date(day);
        runDate.setUTCHours(hour, 0, 0, 0);
        if (runDate.getTime() <= now.getTime()) {
          const dayLabel = dayOffset === 0 ? 'Today' : 'Yesterday';
          runs.push({
            label: `${dayLabel}, ${hour.toString().padStart(2, '0')}:00 UTC`,
            value: runDate.toISOString(),
            date: runDate
          });
        }
      }
    }
    return runs;
  }, []);
  // Hourly runs for HRRR-type models (last 24 hours, every hour)
  const availableHourlyRuns = useMemo(() => {
    const runs: {
      label: string;
      value: string;
      date: Date;
    }[] = [];
    const now = new Date();
    // Generate last 24 hourly runs
    for (let hoursAgo = 0; hoursAgo < 24; hoursAgo++) {
      const runDate = new Date(now);
      runDate.setUTCHours(runDate.getUTCHours() - hoursAgo, 0, 0, 0);
      if (runDate.getTime() <= now.getTime()) {
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setUTCDate(yesterday.getUTCDate() - 1);
        let dayLabel: string;
        if (runDate >= today) {
          dayLabel = 'Today';
        } else if (runDate >= yesterday) {
          dayLabel = 'Yesterday';
        } else {
          dayLabel = runDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          });
        }
        const hour = runDate.getUTCHours();
        runs.push({
          label: `${dayLabel}, ${hour.toString().padStart(2, '0')}:00 UTC`,
          value: runDate.toISOString(),
          date: runDate
        });
      }
    }
    return runs;
  }, []);
  const {
    data: forecastData,
    loading,
    cityName
  } = useWeatherForecast(region);
  // Fetch ENTSOE data when an ENTSOE variable is selected
  const {
    data: entsoeData,
    loading: entsoeLoading,
    error: entsoeError,
    isENTSOE,
    hasRealData: hasENTSOEData
  } = useENTSOEData(variable, region, 2);
  const isLoading = isENTSOE ? entsoeLoading || loading : loading;
  const [menuOpen, setMenuOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [variableOpen, setVariableOpen] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');
  const [modelOpen, setModelOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const regionButtonRef = useRef<HTMLButtonElement>(null);
  const variableButtonRef = useRef<HTMLButtonElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);
  const closeAllDropdowns = () => {
    setRegionOpen(false);
    setVariableOpen(false);
    setVariableSearch('');
    setModelOpen(false);
    setMenuOpen(false);
  };
  // Toggle model: if model has entries, remove all; if not, add with latest run
  const handleToggleModel = (name: string) => {
    setEntries(prev => {
      const hasEntries = prev.some(e => e.modelName === name);
      if (hasEntries) {
        const filtered = prev.filter(e => e.modelName !== name);
        return filtered.length === 0 ? prev : filtered; // keep at least 1
      }
      const runIso = getLatestRunIso(isHourlyModel(name));
      return [...prev, {
        id: makeEntryId(name, runIso),
        modelName: name,
        runIso
      }];
    });
    onParameterChange?.();
  };
  // Toggle a specific run: if that model+run exists, remove it; otherwise add it
  const handleToggleRun = (modelName: string, runValue: string) => {
    setEntries(prev => {
      const entryId = makeEntryId(modelName, runValue);
      const exists = prev.find(e => e.id === entryId);
      if (exists) {
        // Remove this specific entry, but keep at least 1 total
        const filtered = prev.filter(e => e.id !== entryId);
        return filtered.length === 0 ? prev : filtered;
      }
      // Add new entry. If model had a "default" (no run) entry, replace it
      const withoutDefault = prev.filter(e => !(e.modelName === modelName && e.runIso === ''));
      return [...withoutDefault, {
        id: entryId,
        modelName,
        runIso: runValue
      }];
    });
    onParameterChange?.();
  };
  // Clear all models — reset to a single default model
  const handleClearAll = () => {
    const runIso = getLatestRunIso(false);
    setEntries([{
      id: makeEntryId('EPT-2', runIso),
      modelName: 'EPT-2',
      runIso
    }]);
    onParameterChange?.();
  };
  const selectedModelRuns = useMemo(() => entriesToModelRunsMap(entries), [entries]);
  // Unique model count for display
  const uniqueModelCount = useMemo(() => new Set(entries.map(e => e.modelName)).size, [entries]);
  const tableData = useMemo(() => {
    // If we have real ENTSOE data, use it
    if (isENTSOE && hasENTSOEData && entsoeData.length > 0) {
      const cols = timeGrouping === 'Hourly' ? Math.min(12, entsoeData.length) : Math.min(7, Math.floor(entsoeData.length / 24));
      let selectedPoints: typeof entsoeData;
      let timeLabels: string[];
      if (timeGrouping === 'Hourly') {
        selectedPoints = entsoeData.slice(0, cols);
        timeLabels = selectedPoints.map(p => {
          const d = new Date(p.dt * 1000);
          return d.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        });
      } else {
        selectedPoints = [];
        timeLabels = [];
        for (let day = 0; day < cols; day++) {
          const dayPoints = entsoeData.slice(day * 24, (day + 1) * 24);
          if (dayPoints.length === 0) break;
          // Use average of the day
          const avgValue = dayPoints.reduce((sum, p) => sum + p.value, 0) / dayPoints.length;
          const midPoint = dayPoints[Math.floor(dayPoints.length / 2)];
          selectedPoints.push({
            ...midPoint,
            value: avgValue
          });
          const d = new Date(midPoint.dt * 1000);
          timeLabels.push(d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }));
        }
      }
      return entries.map(entry => {
        const seed = entry.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) * 137;
        let s = seed;
        const modelRand = () => {
          s = (s * 16807 + 0) % 2147483647;
          return (s - 1) / 2147483646;
        };
        const values = selectedPoints.map((point, i) => {
          // First model gets real data, others get slight variations
          const entryIndex = entries.indexOf(entry);
          const variation = entryIndex === 0 ? 0 : (modelRand() - 0.5) * Math.abs(point.value) * 0.08;
          return +(point.value + variation).toFixed(1);
        });
        return {
          entry,
          values,
          timeLabels
        };
      });
    }
    // Fall back to weather-derived mock data
    if (!forecastData.length) {
      const cols = timeGrouping === 'Hourly' ? 12 : 7;
      const timeLabels = Array.from({
        length: cols
      }, (_, i) => {
        if (timeGrouping === 'Hourly') return `${i}:00`;
        const d = new Date();
        d.setDate(d.getDate() + i);
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      });
      return entries.map(entry => ({
        entry,
        values: timeLabels.map(() => 0),
        timeLabels
      }));
    }
    const cols = timeGrouping === 'Hourly' ? Math.min(12, forecastData.length) : Math.min(7, Math.floor(forecastData.length / 8));
    let selectedPoints: typeof forecastData;
    let timeLabels: string[];
    if (timeGrouping === 'Hourly') {
      selectedPoints = forecastData.slice(0, cols);
      timeLabels = selectedPoints.map(p => {
        const d = new Date(p.dt * 1000);
        return d.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      });
    } else {
      selectedPoints = [];
      timeLabels = [];
      for (let day = 0; day < cols; day++) {
        const dayPoints = forecastData.slice(day * 8, (day + 1) * 8);
        if (dayPoints.length === 0) break;
        const midPoint = dayPoints[Math.floor(dayPoints.length / 2)];
        selectedPoints.push(midPoint);
        const d = new Date(midPoint.dt * 1000);
        timeLabels.push(d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }));
      }
    }
    return entries.map(entry => {
      // Use entry.id as seed so same model with different runs gets different data
      const seed = entry.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) * 137;
      let s = seed;
      const modelRand = () => {
        s = (s * 16807 + 0) % 2147483647;
        return (s - 1) / 2147483646;
      };
      const values = selectedPoints.map(point => {
        const realValue = getVariableValue(point, variable);
        const variation = (modelRand() - 0.5) * realValue * 0.2;
        return +(realValue + variation).toFixed(1);
      });
      return {
        entry,
        values,
        timeLabels
      };
    });
  }, [forecastData, region, variable, timeGrouping, entries, isENTSOE, hasENTSOEData, entsoeData]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const isInMenu = (target as HTMLElement).closest('[data-portal-menu]');
      if (menuOpen && menuRef.current && !menuRef.current.contains(target) && buttonRef.current && !buttonRef.current.contains(target)) setMenuOpen(false);
      if (regionOpen && !regionButtonRef.current?.contains(target) && !isInMenu) setRegionOpen(false);
      if (variableOpen && !variableButtonRef.current?.contains(target) && !isInMenu) setVariableOpen(false);
      if (modelOpen && !modelButtonRef.current?.contains(target) && !isInMenu) setModelOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, regionOpen, variableOpen, modelOpen]);
  const getMenuPosition = (ref: React.RefObject<HTMLElement>, align: 'left' | 'right' = 'right') => {
    if (!ref.current) return {
      top: 0,
      left: 0
    };
    const rect = ref.current.getBoundingClientRect();
    return align === 'left' ? {
      top: rect.bottom + 4,
      left: rect.left
    } : {
      top: rect.bottom + 4,
      left: rect.right - 176
    };
  };
  const glassStyle: React.CSSProperties = {
    background: 'rgba(11, 17, 32, 0.80)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const dropdownStyle: React.CSSProperties = {
    zIndex: 9999,
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const getCellColor = (val: number, colMin: number, colMax: number, colAvg: number) => {
    const range = colMax - colMin;
    // No highlighting if all values are essentially the same
    if (range < 0.3) {
      return {
        bg: 'transparent',
        text: 'text-slate-400'
      };
    }
    // Only highlight the actual min and max of the column (true outliers)
    const isMax = val === colMax;
    const isMin = val === colMin;
    // Require the outlier to be meaningfully far from average (>15% of range)
    const deviationFromAvg = Math.abs(val - colAvg);
    const isSignificant = deviationFromAvg > range * 0.15;
    if (isMax && isSignificant) {
      return {
        bg: 'rgba(219, 39, 119, 0.18)',
        text: 'text-pink-400'
      };
    }
    if (isMin && isSignificant) {
      return {
        bg: 'rgba(59, 130, 246, 0.18)',
        text: 'text-blue-400'
      };
    }
    return {
      bg: 'transparent',
      text: 'text-slate-400'
    };
  };
  // Display label: always show run info
  const getEntryLabel = (entry: ModelRunEntry): {
    name: string;
    runLabel: string;
  } => {
    const sameModelEntries = entries.filter(e => e.modelName === entry.modelName);
    const runLabel = entry.runIso ? formatRunLabel(entry.runIso) : 'Latest';
    // Show run suffix in name when multiple entries of same model
    const name = sameModelEntries.length > 1 && entry.runIso ? `${entry.modelName} (${formatRunShort(entry.runIso)})` : entry.modelName;
    return {
      name,
      runLabel
    };
  };
  return <WidgetWrapper ref={ref} title={`${region} - ${variable}`} onRemove={onRemove} onDoubleClickHandle={onDoubleClickHandle} readOnly={readOnly} style={style} className={className} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onTouchEnd={onTouchEnd} {...props} data-id="element-2055">
        <div className="flex flex-col h-full jua-widget-body" data-id="element-2056">
          {/* Toolbar — hidden in readOnly */}
          {!readOnly && <div className="relative z-30 flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] gap-2 min-w-0" style={glassStyle} data-id="element-2057">
              <div className="flex items-center gap-2 min-w-0" data-id="element-2058">
                <Table2 size={14} className="text-[#EC4899] flex-shrink-0" data-id="element-2059" />

                {/* Region */}
                <div className="relative" data-id="element-2060">
                  <button ref={regionButtonRef} onClick={() => {
              closeAllDropdowns();
              setRegionOpen(!regionOpen);
            }} className="jua-widget-dropdown-btn h-7 px-2 rounded-lg text-[11px] font-medium text-white/80 border border-white/[0.06] bg-white/[0.04] hover:bg-white/[0.10] hover:border-white/[0.12] backdrop-blur-sm flex items-center gap-1.5 transition-all whitespace-nowrap shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]" data-id="element-2061">
                    <span className="truncate" data-id="element-2062">{region}</span>
                    <ChevronDown size={10} className="text-white/40 flex-shrink-0" data-id="element-2063" />
                  </button>
                  {regionOpen && ReactDOM.createPortal(<RegionSelector value={region} onChange={r => {
              setRegion(r);
              onParameterChange?.();
            }} onClose={() => setRegionOpen(false)} style={{
              zIndex: 9999,
              top: getMenuPosition(regionButtonRef, 'left').top,
              left: getMenuPosition(regionButtonRef, 'left').left
            }} data-id="element-2064" />, document.body)}
                </div>

                {/* Variable */}
                <div className="relative" data-id="element-2065">
                  <button ref={variableButtonRef} onClick={() => {
              closeAllDropdowns();
              setVariableOpen(!variableOpen);
            }} className="h-7 px-2 rounded-md text-[11px] font-medium text-white/90 border border-white/[0.1] bg-white/[0.06] hover:bg-white/[0.1] flex items-center gap-1.5 transition-colors whitespace-nowrap max-w-[160px]" data-id="element-2066">
                    <span className="truncate" data-id="element-2067">{variable}</span>
                    <ChevronDown size={10} className="text-white/50 flex-shrink-0" data-id="element-2068" />
                  </button>
                  {variableOpen && ReactDOM.createPortal(<div data-portal-menu className="fixed w-64 rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden flex flex-col" style={{
              ...dropdownStyle,
              top: getMenuPosition(variableButtonRef, 'left').top,
              left: getMenuPosition(variableButtonRef, 'left').left,
              maxHeight: '420px'
            }} data-id="element-2069">
                        <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
              }} data-id="element-2070" />
                        {/* Search input */}
                        <div className="relative px-2 pt-2 pb-1.5 border-b border-white/[0.06] flex-shrink-0" data-id="element-2071">
                          <input autoFocus type="text" value={variableSearch} onChange={e => setVariableSearch(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const results = filterAndSortVariables(VARIABLES, variableSearch);
                    if (results.length > 0) {
                      setVariable(results[0]);
                      setVariableOpen(false);
                      setVariableSearch('');
                      onParameterChange?.();
                    }
                  }
                  if (e.key === 'Escape') {
                    setVariableOpen(false);
                    setVariableSearch('');
                  }
                }} placeholder="Search variables…" className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-[11px] text-white/90 placeholder:text-white/25 focus:outline-none focus:border-[#EC4899]/40" data-id="element-2072" />
                        </div>
                        {/* Variable list */}
                        <div className="relative overflow-y-auto flex-1 py-1" data-id="element-2073">
                          {(() => {
                  const filtered = filterAndSortVariables(VARIABLES, variableSearch);
                  if (filtered.length === 0) {
                    return <div className="px-3 py-4 text-center text-[11px] text-white/30" data-id="element-2074">
                                  No variables match "{variableSearch}"
                                </div>;
                  }
                  // When searching, show flat filtered list
                  if (variableSearch.trim()) {
                    return filtered.map((v, i) => <button key={v} onClick={() => {
                      setVariable(v);
                      setVariableOpen(false);
                      setVariableSearch('');
                      onParameterChange?.();
                    }} className={`relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors ${i === 0 ? 'bg-white/[0.04]' : ''}`} data-id="element-2075">
                                  <span data-id="element-2076">{v}</span>
                                  <div className="flex items-center gap-1.5" data-id="element-2077">
                                    {i === 0 && <span className="text-[9px] text-[#EC4899]/70 font-medium" data-id="element-2078">
                                        best match
                                      </span>}
                                    {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-2079" />}
                                  </div>
                                </button>);
                  }
                  // Default grouped view
                  const groups: {
                    label: string;
                    labelColor: string;
                    items: string[];
                  }[] = [{
                    label: 'Temperature',
                    labelColor: 'text-white/30',
                    items: ['Temp 2m', 'Dew Point 2m']
                  }, {
                    label: 'Wind',
                    labelColor: 'text-white/30',
                    items: ['Wind Speed 10m', 'Wind Dir 10m', 'Wind Speed 100m', 'Wind Dir 100m']
                  }, {
                    label: 'Precip / Radiation / Atmos',
                    labelColor: 'text-white/30',
                    items: ['Precipitation 1h', 'SSRD 1h', 'MSL Pressure']
                  }, {
                    label: 'Cloud Cover',
                    labelColor: 'text-white/30',
                    items: ['Cloud Cover High', 'Cloud Cover Medium', 'Cloud Cover Low']
                  }, {
                    label: 'ENTSOE — Prices',
                    labelColor: 'text-amber-400/50',
                    items: ['Day-ahead Prices', 'Imbalance Prices']
                  }, {
                    label: 'ENTSOE — Generation',
                    labelColor: 'text-amber-400/50',
                    items: ['Generation (Actual)', 'Generation Forecast (Day-ahead)', 'Wind & Solar Forecast (Day-ahead)', 'Wind & Solar Forecast (Intraday)']
                  }, {
                    label: 'ENTSOE — Load',
                    labelColor: 'text-amber-400/50',
                    items: ['Load (Actual)', 'Load Forecast (Day-ahead)']
                  }, {
                    label: 'ENTSOE — Capacity & Flows',
                    labelColor: 'text-amber-400/50',
                    items: ['Available Hydro Capacity', 'Cross-border Flows', 'NTC (Day-ahead)', 'NTC Weekahead', 'NTC Monthahead', 'NTC Yearahead']
                  }, {
                    label: 'ENTSOE — Balancing',
                    labelColor: 'text-amber-400/50',
                    items: ['Imbalance Volumes', 'Net Position (Day-ahead)', 'Net Position (Total)', 'Scheduled Exchanges (Day-ahead)', 'Scheduled Exchanges (Total)']
                  }];
                  return groups.map(({
                    label,
                    labelColor,
                    items
                  }, gi) => <Fragment key={label} data-id="element-2080">
                                  {gi > 0 && <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-2081" />}
                                  <div className={`relative px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-wider ${labelColor}`} data-id="element-2082">
                                    {label}
                                  </div>
                                  {items.map(v => <button key={v} onClick={() => {
                      setVariable(v);
                      setVariableOpen(false);
                      setVariableSearch('');
                      onParameterChange?.();
                    }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-2083">
                                      {v}
                                      {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-2084" />}
                                    </button>)}
                                </Fragment>);
                })()}
                        </div>
                      </div>, document.body)}
                </div>

                {/* Model */}
                <div className="relative" data-id="element-2085">
                  <button ref={modelButtonRef} onClick={() => {
              closeAllDropdowns();
              setModelOpen(!modelOpen);
            }} className="jua-widget-dropdown-btn h-7 px-2 rounded-lg text-[11px] font-medium text-white/80 border border-white/[0.06] bg-white/[0.04] hover:bg-white/[0.10] hover:border-white/[0.12] backdrop-blur-sm flex items-center gap-1.5 transition-all whitespace-nowrap shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]" data-id="element-2086">
                    <Cpu size={10} className="text-white/50 flex-shrink-0" data-id="element-2087" />
                    <span className="truncate" data-id="element-2088">
                      {uniqueModelCount} model
                      {uniqueModelCount !== 1 ? 's' : ''}
                      {entries.length > uniqueModelCount ? ` · ${entries.length} runs` : ''}
                    </span>
                    <ChevronDown size={10} className="text-white/50 flex-shrink-0" data-id="element-2089" />
                  </button>
                  {modelOpen && ReactDOM.createPortal(<TableModelDropdown allModels={ALL_MODELS} selectedModelRuns={selectedModelRuns} onToggleModel={handleToggleModel} onToggleRun={handleToggleRun} onClearAll={handleClearAll} availableRuns={availableRuns} availableHourlyRuns={availableHourlyRuns} style={{
              ...dropdownStyle,
              top: getMenuPosition(modelButtonRef, 'left').top,
              left: getMenuPosition(modelButtonRef, 'left').left
            }} data-id="element-2090" />, document.body)}
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-2091">
                <div className="flex bg-white/[0.06] rounded-md p-0.5 border border-white/[0.1]" data-id="element-2092">
                  {TIME_GROUPINGS.map(t => <button key={t} onClick={() => {
              setTimeGrouping(t);
              onParameterChange?.();
            }} className={`px-2 py-0.5 text-[10px] font-medium rounded-sm transition-all ${timeGrouping === t ? 'bg-[#EC4899] text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`} data-id="element-2093">
                      {t}
                    </button>)}
                </div>
                <div className="relative" data-id="element-2094">
                  <button ref={buttonRef} className="p-1.5 text-white/40 hover:text-white/80 transition-colors rounded-md hover:bg-white/[0.06]" onClick={() => {
              setMenuOpen(!menuOpen);
              setRegionOpen(false);
              setVariableOpen(false);
              setModelOpen(false);
            }} data-id="element-2095">
                    <MoreVertical size={14} data-id="element-2096" />
                  </button>
                  {menuOpen && ReactDOM.createPortal(<div ref={menuRef} data-id="element-2097">
                        <TableActionsMenu onDuplicate={onDuplicate} onRemove={onRemove} onClose={() => setMenuOpen(false)} style={{
                ...dropdownStyle,
                top: getMenuPosition(buttonRef, 'right').top,
                left: getMenuPosition(buttonRef, 'right').left
              }} data-id="element-2098" />
                      </div>, document.body)}
                </div>
              </div>
            </div>}

          {/* Table */}
          <div className="flex-1 overflow-auto p-0" data-id="element-2099">
            {isLoading ? <div className="h-full flex flex-col items-center justify-center gap-3" data-id="element-2100">
                <div className="w-5 h-5 border-2 border-[#EC4899] border-t-transparent rounded-full animate-spin" data-id="element-2101" />
                <span className="text-xs text-slate-400" data-id="element-2102">
                  Loading {isENTSOE ? 'ENTSOE' : ''} {cityName || region}{' '}
                  data...
                </span>
              </div> : <table className="w-full text-xs border-collapse" data-id="element-2103">
                <thead className="sticky top-0 jua-table-head z-10 shadow-sm" data-id="element-2104">
                  <tr data-id="element-2105">
                    <th className="text-left py-2.5 px-4 font-medium text-slate-400 border-b border-white/[0.06] w-44 sticky left-0 bg-[#0B1120] z-10" data-id="element-2106">
                      <div className="flex items-center gap-1.5" data-id="element-2107">
                        Model
                        {modelOverride && <span className="flex items-center gap-1 text-[9px] font-medium text-amber-400/70 bg-amber-500/10 px-1.5 py-0.5 rounded" data-id="element-2108">
                            <Lock size={8} data-id="element-2109" /> Override
                          </span>}
                      </div>
                    </th>
                    {tableData[0]?.timeLabels.map((label, i) => <th key={i} className="text-center py-2.5 px-2 font-medium text-slate-400 border-b border-white/[0.06] min-w-[60px]" data-id="element-2110">
                        {label}
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-2111">
                  {tableData.map((row, i) => {
              const {
                entry
              } = row;
              const {
                name,
                runLabel
              } = getEntryLabel(entry);
              const isOverridden = !!modelOverride;
              return <tr key={entry.id} className={`group transition-colors ${isOverridden ? 'hover:bg-amber-500/[0.04]' : 'hover:bg-white/[0.02]'}`} data-id="element-2112">
                        <td className={`py-2 px-4 border-b border-white/[0.04] font-medium text-slate-200 sticky left-0 transition-colors jua-table-cell-default ${isOverridden ? 'bg-[#1a1608] group-hover:bg-[#1f1b0a]' : 'bg-[#0B1120] group-hover:bg-[#0F172A]'}`} data-id="element-2113">
                          <div className="flex items-center gap-2" data-id="element-2114">
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getModelColor(entry.modelName, i)}`} data-id="element-2115" />
                            <div className="flex flex-col min-w-0" data-id="element-2116">
                              <div className="flex items-center gap-1.5" data-id="element-2117">
                                {isOverridden && <Lock size={9} className="text-amber-400/70 flex-shrink-0" data-id="element-2118" />}
                                <span className="truncate" data-id="element-2119">{name}</span>
                              </div>
                              {runLabel && <span className={`text-[9px] font-mono whitespace-nowrap ${isOverridden ? 'text-amber-400/50' : 'text-white/30'}`} data-id="element-2120">
                                  {runLabel}
                                </span>}
                            </div>
                          </div>
                        </td>
                        {row.values.map((val, j) => {
                  const colValues = tableData.map(r => r.values[j]);
                  const colAvg = colValues.reduce((a, b) => a + b, 0) / colValues.length;
                  const colMin = Math.min(...colValues);
                  const colMax = Math.max(...colValues);
                  const {
                    bg,
                    text
                  } = getCellColor(val, colMin, colMax, colAvg);
                  return <td key={j} className="py-2 px-2 border-b border-white/[0.04] text-center" data-id="element-2121">
                              <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${text}`} style={{
                      backgroundColor: bg
                    }} data-id="element-2122">
                                {val.toFixed(1)}
                              </span>
                            </td>;
                })}
                      </tr>;
            })}
                </tbody>
              </table>}
          </div>

          {/* Footer — hidden in readOnly */}
          {!readOnly && <div className="px-4 py-2 flex items-center justify-between text-xs border-t border-white/[0.06]" style={glassStyle} data-id="element-2123">
              <div className="flex items-center gap-2 text-white/50" data-id="element-2124">
                <Database size={12} data-id="element-2125" />
                <span className="font-medium text-white/70" data-id="element-2126">
                  {cityName || region}
                </span>
                <span className="text-white/20" data-id="element-2127">|</span>
                <span data-id="element-2128">
                  {variable} ({getVariableUnit(variable)})
                </span>
                <span className="text-white/20" data-id="element-2129">|</span>
                <span data-id="element-2130">
                  {entries.length} run{entries.length !== 1 ? 's' : ''}
                </span>
                <span className="text-white/20" data-id="element-2131">|</span>
                {modelOverride ? <span className="text-amber-400/80 flex items-center gap-1" data-id="element-2132">
                    <Lock size={10} data-id="element-2133" /> Global Override
                  </span> : isENTSOE && hasENTSOEData ? <span className="text-amber-400/70 flex items-center gap-1" data-id="element-2134">
                    ⚡ ENTSOE Live
                  </span> : isENTSOE && entsoeError ? <span className="text-orange-400/70 flex items-center gap-1" title={entsoeError} data-id="element-2135">
                    ⚠ ENTSOE fallback (mock)
                  </span> : <span className="text-emerald-400/70" data-id="element-2136">Live</span>}
              </div>
              <Info size={12} className="text-white/30" data-id="element-2137" />
            </div>}
        </div>
      </WidgetWrapper>;
});